import {useRef} from 'react';

import {FIRST_ITEM_INDEX_INITIAL} from '../constants';
import {type LogLineData} from '../LogLineRenderer';

/**
 * 'useFirstItemIndex' tracks the initial (logical) index of the provided log
 * 'lines' array so that Virtuoso can maintain a stable scroll position as older
 * logs are prepended.
 */
export function useFirstItemIndex(
  lines: readonly LogLineData[],
  isLoadingPrevious: boolean,
  initialValue: number = FIRST_ITEM_INDEX_INITIAL
): number {
  // NOTE: Use 'useRef' so that 'firstItemIndex' can be updated synchronously
  // with 'lines' during a render (state can't be updated during a render).
  const firstItemIndex = useRef(initialValue);
  const prevLinesLength = useRef(lines.length);
  const wasLoadingPrevious = useRef(false);

  // Check whether an increase in 'lines' length came from a prepend operation.
  // Both 'isLoadingPrevious' and 'wasLoadingPrevious' are checked because
  // 'lines' and 'isLoadingPrevious' might be updated in the same render.
  // oxlint-disable-next-line react/refs
  const isPrepending = isLoadingPrevious || wasLoadingPrevious.current;

  // oxlint-disable-next-line react/refs
  if (isPrepending && lines.length > prevLinesLength.current) {
    // oxlint-disable-next-line react/refs
    const count = lines.length - prevLinesLength.current;
    // oxlint-disable-next-line react/refs
    firstItemIndex.current -= count;
  }

  // oxlint-disable-next-line react/refs
  prevLinesLength.current = lines.length;
  // oxlint-disable-next-line react/refs
  wasLoadingPrevious.current = isLoadingPrevious;

  // oxlint-disable-next-line react/refs
  return firstItemIndex.current;
}
